/**
 * AdminPessoasPage — Consolidated People management (Usuários, Departamentos, Hierarquia, Cargos)
 * Tab switching via ribbon Pessoas dropdown (?tab= query param). Per-tab actions in ribbon.
 */
import React, { useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, ShieldCheck, ArrowRightLeft, Building2, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { AdminUsersPage } from './AdminUsersPage';
import { AdminDepartmentsPage } from './AdminDepartmentsPage';
import { AdminHierarchyPage } from './AdminHierarchyPage';
import { AdminRolesPage } from './AdminRolesPage';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';
import { AdminRibbonCtaButton, adminRibbonFilterBtnClass } from '../components/AdminRibbonCtaButton';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { getDepartments } from '../../../services/organizationService';

const ROLE_LABELS: Record<string, string> = {
  owner:    'Owner (Proprietário)',
  admin:    'Administrador',
  operator: 'Operador',
  viewer:   'Visualizador',
};

export const AdminPessoasPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'usuarios';
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';

  // Fetch departments for cargos filter
  const deptsQuery = useQuery({
    queryKey: ['admin-departments-pessoas', orgId],
    queryFn: () => getDepartments(orgId),
    enabled: !!orgId,
  });
  const departments = deptsQuery.data ?? [];

  // Shared state lifted from sub-pages
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');       // cargos: filter by department
  const [deptManagerFilter, setDeptManagerFilter] = useState('all'); // departamentos: filter by manager

  // Extract unique manager names from departments
  const managerNames = useMemo(() => {
    const names = new Set<string>();
    for (const d of departments as any[]) {
      if (d.manager_name) names.add(d.manager_name);
    }
    return Array.from(names).sort();
  }, [departments]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateDept, setShowCreateDept] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [toggleBatch, setToggleBatch] = useState(false);

  // Reset create states on tab switch
  const triggerCreateDept = useCallback(() => setShowCreateDept(true), []);
  const triggerCreateRole = useCallback(() => setShowCreateRole(true), []);

  const ribbonPrimaryAction = useMemo(() => {
    switch (activeTab) {
      case 'usuarios':
        return (
          <AdminRibbonCtaButton
            label="Novo Usuário"
            title="Convidar usuário para a plataforma"
            onClick={() => setShowInviteModal(true)}
          />
        );
      case 'departamentos':
        return <AdminRibbonCtaButton label="Novo Departamento" onClick={triggerCreateDept} />;
      case 'cargos':
        return <AdminRibbonCtaButton label="Novo Cargo" onClick={triggerCreateRole} />;
      case 'hierarquia':
        return (
          <>
            <AdminRibbonCtaButton label="Novo Membro" onClick={() => setShowAddMember(true)} />
            <button
              type="button"
              className={`${adminRibbonFilterBtnClass} ${toggleBatch ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-soft)]' : ''}`}
              onClick={() => setToggleBatch((prev) => !prev)}
            >
              <ArrowRightLeft size={12} className="text-[var(--text-secondary)]" />
              Reorganizar
            </button>
          </>
        );
      default:
        return null;
    }
  }, [activeTab, triggerCreateDept, triggerCreateRole, toggleBatch]);

  const filtersWidget = useMemo(() => {
    return (
      <div className="flex flex-1 items-center justify-end gap-1.5">
        {ribbonPrimaryAction}
        {/* Role filter — usuarios tab */}
        {activeTab === 'usuarios' && (
          <div className="relative">
            <select
              className={`${adminRibbonFilterBtnClass} appearance-none cursor-pointer`}
              style={{ paddingLeft: 22, paddingRight: 22 }}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">Todos papéis</option>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <ShieldCheck size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          </div>
        )}

        {/* Manager filter — departamentos tab */}
        {activeTab === 'departamentos' && (
          <div className="relative">
            <select
              className={`${adminRibbonFilterBtnClass} appearance-none cursor-pointer`}
              style={{ paddingLeft: 22, paddingRight: 22 }}
              value={deptManagerFilter}
              onChange={(e) => setDeptManagerFilter(e.target.value)}
            >
              <option value="all">Todos gestores</option>
              <option value="none">Sem gestor</option>
              {managerNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <Users size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          </div>
        )}

        {/* Department filter — cargos tab */}
        {activeTab === 'cargos' && (
          <div className="relative">
            <select
              className={`${adminRibbonFilterBtnClass} appearance-none cursor-pointer`}
              style={{ paddingLeft: 22, paddingRight: 22 }}
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="all">Todos deptos</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <Building2 size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          </div>
        )}

        {/* Search bar */}
        {(activeTab === 'usuarios' || activeTab === 'cargos' || activeTab === 'departamentos') && (
          <div className="flex w-[220px] shrink-0 items-center">
            <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
              <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
              <input
                type="text"
                placeholder={activeTab === 'usuarios' ? 'Buscar nome ou e-mail...' : 'Buscar...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
              />
              {search && (
                <button onClick={() => setSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
                  <X size={10} className="text-[var(--text-secondary)]" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }, [activeTab, roleFilter, deptFilter, deptManagerFilter, search, departments, managerNames, ribbonPrimaryAction]);

  return (
    <AdminConsoleLayout ribbonSlots={{ afterGroups: filtersWidget }}>
      <div className="h-full flex flex-col overflow-hidden">
        {activeTab === 'usuarios' && (
          <AdminUsersPage
            externalSearch={search}
            externalRoleFilter={roleFilter}
            externalShowInviteModal={showInviteModal}
            onCloseInviteModal={() => setShowInviteModal(false)}
          />
        )}
        {activeTab === 'departamentos' && (
          <AdminDepartmentsPage
            externalShowCreate={showCreateDept}
            onCreateDone={() => setShowCreateDept(false)}
            externalSearch={search}
            externalManagerFilter={deptManagerFilter}
          />
        )}
        {activeTab === 'hierarquia' && (
          <AdminHierarchyPage
            externalShowAddModal={showAddMember}
            onCloseAddModal={() => setShowAddMember(false)}
            externalToggleBatch={toggleBatch}
            onBatchDone={() => setToggleBatch(false)}
          />
        )}
        {activeTab === 'cargos' && (
          <AdminRolesPage
            externalShowCreate={showCreateRole}
            onCreateDone={() => setShowCreateRole(false)}
            externalSearch={search}
            externalDeptFilter={deptFilter}
          />
        )}
      </div>
    </AdminConsoleLayout>
  );
};
